// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, {useCallback, useEffect, useRef} from 'react';
import {
    Container,
    ExpandableSection,
    Header,
    SpaceBetween,
} from '@cloudscape-design/components';
import Breadcrumbs from '../../../../../Utils/Breadcrumbs';
import {DRAW, OPEN_DIAGRAM} from '../../../../../routes';
import {useParams} from 'react-router-dom';
import ResourceSearch from '../../Utils/ResourceSearch';
import DiagramControlPanel from '../DiagramControlsPanel';
import PureCytoscape from '../../Canvas/PureCytoscape';
import {useDiagramSettingsState} from '../../../../Contexts/DiagramSettingsContext';
import {
    addResources,
    removeResource,
} from '../../Canvas/Commands/CanvasCommands';
import {getStandardLayout} from '../../Canvas/Layout/StandardGraphLayout';
import DiagramSettings from '../../Utils/DiagramSettings';
import {useObject} from '../../../../Hooks/useS3Objects';
import * as R from 'ramda';
import FilterControlsUI from '../../../../Explore/Shared/FilterControlsUI';
import {useFilterPersistence} from '../../../../Contexts/FilterPersistenceContext';
import {useExperimentsState} from '../../../../Contexts/ExperimentsContext';
import {
    applyFiltersToCanvas,
    useDebounce,
    createDefaultDiagramSettings,
    applyDiagramSettingsFilters,
    showAllCanvasElements,
} from '../../../../../Utils/DiagramFilteringUtils';

const DIAGRAMS = 'diagrams/';

const OpenDiagramPage = () => {
    const [{canvas}, dispatchCanvas] = useDiagramSettingsState();
    const [settings, setSettings] = React.useState(null);
    const {name, visibility} = useParams();
    const {data} = useObject(name, DIAGRAMS, visibility);
    const {experimentsEnabled} = useExperimentsState();
    const {
        viewMode,
        excludePrefixes,
        keywordFilter,
        hideAutoGenerated,
        updateFilter,
    } = useFilterPersistence();

    // Debounce text filter inputs to reduce re-renders
    const debouncedExcludePrefixes = useDebounce(excludePrefixes, 300);
    const debouncedKeywordFilter = useDebounce(keywordFilter, 300);

    // Refs for tracking state and preventing infinite loops
    const initialLoadComplete = useRef(false);

    // Stable callback references
    const updateCanvas = useCallback(
        (newCanvas) => {
            if (dispatchCanvas) {
                dispatchCanvas({
                    type: 'setCanvas',
                    canvas: newCanvas,
                });
            }
        },
        [dispatchCanvas]
    );

    const updateResources = useCallback(() => {
        if (canvas && !canvas.destroyed() && dispatchCanvas) {
            dispatchCanvas({
                type: 'setResources',
                resources: canvas.nodes(),
            });
        }
    }, [canvas, dispatchCanvas]);

    // Effect 1 - Reset initialization flag when navigating to different diagram
    useEffect(() => {
        initialLoadComplete.current = false;
    }, [name, visibility]);

    // Effect 2 - Reset filters when experiments disabled
    // Added guard to prevent unnecessary updates
    useEffect(() => {
        if (!experimentsEnabled && viewMode !== 'all') {
            updateFilter('viewMode', 'all');
        }
    }, [experimentsEnabled, viewMode, updateFilter]);

    // Effect 3 - Initialize canvas (runs once per diagram)
    // Includes all dependencies - callbacks are stable due to useCallback
    useEffect(() => {
        if (!data || !canvas || canvas.destroyed() || initialLoadComplete.current) {
            return;
        }

        try {
            setSettings(createDefaultDiagramSettings(data.settings));
            addResources(
                canvas,
                updateCanvas,
                updateResources,
                R.pick(['nodes', 'edges'], data),
                getStandardLayout
            );
            initialLoadComplete.current = true;
        } catch (error) {
            console.error('Error initializing canvas:', error);
            // Reset flag to allow retry on next render
            initialLoadComplete.current = false;
        }
    }, [canvas, data, updateCanvas, updateResources]);

    // Effect 4 - Apply destructive settings filters (removes nodes permanently)
    // Only runs when settings change to avoid removing already-removed nodes
    useEffect(() => {
        if (!canvas || canvas.destroyed() || !initialLoadComplete.current || !settings) {
            return;
        }

        try {
            applyDiagramSettingsFilters(
                canvas,
                settings,
                updateCanvas,
                updateResources,
                removeResource
            );
        } catch (error) {
            console.error('Error applying settings filters:', error);
        }
    }, [canvas, settings, updateCanvas, updateResources]);

    // Effect 5 - Apply non-destructive view filters (toggles display: none)
    // Runs on every view filter change for responsive filtering
    useEffect(() => {
        if (!canvas || canvas.destroyed() || !initialLoadComplete.current) {
            return;
        }

        try {
            if (!experimentsEnabled) {
                showAllCanvasElements(canvas);
            } else {
                const filterState = {
                    viewMode,
                    hideAutoGenerated,
                    excludePrefixes: debouncedExcludePrefixes,
                    keywordFilter: debouncedKeywordFilter,
                };
                applyFiltersToCanvas(canvas, filterState);
            }
        } catch (error) {
            console.error('Error applying view filters:', error);
        }
    }, [
        canvas,
        experimentsEnabled,
        viewMode,
        hideAutoGenerated,
        debouncedExcludePrefixes,
        debouncedKeywordFilter
    ]);

    return (
        <SpaceBetween size="l">
            <Breadcrumbs
                items={[
                    {text: 'Diagrams', href: DRAW},
                    {text: name, href: OPEN_DIAGRAM},
                ]}
            />
            <Container
                header={
                    <Header
                        variant="h2"
                        actions={
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                }}
                            >
                                {experimentsEnabled && <FilterControlsUI />}
                                <DiagramControlPanel settings={settings} />
                            </div>
                        }
                    >
                        {name}
                    </Header>
                }
            >
                <SpaceBetween size={'l'}>
                    <ResourceSearch />
                    <PureCytoscape name={name} visibility={visibility} />
                    {settings && (
                        <ExpandableSection header={'Diagram Settings'}>
                            <DiagramSettings
                                initialSettings={settings}
                                onSettingsChange={setSettings}
                            />
                        </ExpandableSection>
                    )}
                </SpaceBetween>
            </Container>
        </SpaceBetween>
    );
};

export default OpenDiagramPage;
